import { useFormatTreatmentPrice } from "~/hec/telehealth/use-format-treatment-price";
import { cn } from "~/lib/ui";
import type { PriceDisplay } from "~/shop/utils/price-display";

const PRICE_CLASS =
  "font-grotesque text-[1.75rem] font-medium leading-none text-product-price";

/**
 * ET-216 — price rendering for telehealth treatment surfaces: an optional
 * prefix ("Starts at"), the price, a recurring-period suffix ("/mo"), and an
 * optional trailing qualifier ("1st month", ET-394).
 *
 * Deliberately NOT built on the shared `<ProductPrice>`: the period suffix has
 * to be placeable on the struck "was" alone — ET-220/221 render
 * `~~$499/mo~~ $475`, a one-off first month against a monthly regular price —
 * which can't be injected from outside that component's on-sale branch.
 * Extending `ProductPrice` would have meant editing the renderer the cart,
 * minicart and checkout all share, for a suffix only treatment PDPs use, so
 * this stays local instead.
 *
 * Which prices the suffix lands on is the caller's call (`periodScope`), because
 * the two discount sources are indistinguishable HERE: `display.isOnSale` is
 * true both for that promo and for an ordinary catalog markdown (AB pricebook /
 * promotion, arriving through `c_priceInfo` list > sales). Rendering a markdown
 * on a monthly treatment as `~~$499/mo~~ $475` would imply the $475 is a
 * one-off charge when it is in fact the new recurring price.
 *
 * It duplicates the was/now LAYOUT, not the pricing decision: which number is
 * struck still comes from `wasNowDisplay` / `resolvePriceFromSelectedVariant`,
 * and formatting still goes through `useFormatPrice`.
 *
 * No `setPriceText` branch — that's a set-product concern (`c_plpSetPrice`), and
 * treatments are never sets.
 */
export function TreatmentPrice({
  display,
  period,
  periodScope = "both",
  prefix,
  trailing,
  className,
  "data-testid": dataTestId = "pdp-price",
}: {
  display: PriceDisplay | null;
  /** e.g. "/mo". Null/undefined renders no suffix. */
  period?: string | null;
  /**
   * Which prices `period` applies to when a discount is showing.
   *
   * - `"both"` (default) — the discount is a MARKDOWN of the recurring charge,
   *   so both figures are per-period: `~~$499/mo~~ $475/mo`. Correct for every
   *   catalog-priced path (AB pricebook, promotions), which is what the coded
   *   column and the block's "use default price" mode both render.
   * - `"regular"` — the discount is a ONE-OFF that the period does not describe:
   *   `~~$499/mo~~ $475`. ET-220/221's first-month promo, which is authored by
   *   hand precisely because the catalog can't express it.
   *
   * Defaulting to `"both"` keeps the safe reading for the case the caller can't
   * identify: a per-period suffix on a per-period price is never wrong, whereas
   * dropping it implies a one-off charge that was never offered.
   */
  periodScope?: "both" | "regular";
  /** e.g. "Starts at". */
  prefix?: string;
  /**
   * A qualifier AFTER the price — "1st month" in `Starts at $475 1st month`
   * (ET-394).
   *
   * Distinct from {@link period}, which is glued to its figure (`$499/mo`) and
   * is `periodScope`-controlled so it can land on the struck price alone. This
   * is a separate word, and it only ever describes the "now" price. Expressing
   * it through `period` was the workaround available before this prop existed,
   * and it required a hand-typed leading space — miss it and the page renders
   * `$4751st month`.
   */
  trailing?: string;
  className?: string;
  "data-testid"?: string;
}) {
  // Whole dollars — `$1,249/mo`, not `$1,249.00/mo`. Shared with
  // `TreatmentPlanSelector`; the rule and its caveats live on the hook.
  const money = useFormatTreatmentPrice();
  if (!display) return null;

  const suffix = period ?? "";
  // Explicit space, not a flex gap: a gap is invisible to `textContent`, so
  // assistive tech would read "Starts at$199.00".
  const lead = prefix ? `${prefix} ` : "";
  // The same rule on the trailing edge — `$475 1st month`, never `$4751st
  // month`. The on-sale branch below doesn't use this for its visible span (a
  // flex item drops leading whitespace anyway, and the gap handles the spacing
  // there); it uses it for the `aria-label`, which is that branch's only
  // accessible name.
  const trail = trailing ? ` ${trailing}` : "";

  if (display.isRange && display.priceMax != null) {
    const low = display.priceMin ?? display.price;
    return (
      <span className={cn(PRICE_CLASS, className)} data-testid={dataTestId}>
        {lead}
        {money(low)} - {money(display.priceMax)}
        {suffix}
        {trail}
      </span>
    );
  }

  if (display.isOnSale && display.compareAtPrice != null) {
    const was = `${money(display.compareAtPrice)}${suffix}`;
    const now = `${money(display.price)}${
      periodScope === "both" ? suffix : ""
    }`;
    return (
      <span
        className={cn(
          "flex flex-wrap items-baseline gap-x-[0.5ch] gap-y-0",
          PRICE_CLASS,
          className,
        )}
        data-testid={dataTestId}
        aria-label={`${lead}Was ${was}, now ${now}${trail}`}
      >
        {lead ? <span aria-hidden>{lead}</span> : null}
        <span aria-hidden className="text-gray-500 line-through">
          {was}
        </span>
        <span aria-hidden className="font-bold text-sale-price">
          {now}
        </span>
        {/* Outside the sale-price span: the qualifier isn't part of the
            discounted figure, so it doesn't take its weight or colour. */}
        {trailing ? <span aria-hidden>{trailing}</span> : null}
      </span>
    );
  }

  return (
    <span className={cn(PRICE_CLASS, className)} data-testid={dataTestId}>
      {lead}
      {money(display.price)}
      {suffix}
      {trail}
    </span>
  );
}
